import type { UseHome } from "./lib";
import { Fragment, useMemo } from "react";
import { isQuerySupported } from "@vzbl/integrations";
import {
  BoardCardData,
  DeepLinkType,
  DigestSection,
  DigestV2State,
  isExploreCard,
} from "pages/api/deep-links/types";
import { useApplicationContext } from "src/hooks/useApplication";
import { useDeepLinksData } from "src/hooks/useDeepLinks";
import { useIntegrationConfig } from "src/search/lib/useIntegrationConfig";
import { DigestSectionCard } from "./DigestSectionCard";
import { Condition, SearchState } from "src/search/types";
import { figureQueryFragment } from "../topics/[topicId]/edit.page";

export function Digest({ home }: { home: UseHome }) {
  const app = useApplicationContext();
  const integrationConfig = useIntegrationConfig().data as any;

  // get the digests
  const digests = useDeepLinksData(DeepLinkType.DIGEST_2);

  const pulseItems = useMemo(() => {
    const sections =
      digests.links.data?.map((d) =>
        toSection(d.name, d.json as DigestV2State)
      ) || [];
    return "integrations" in integrationConfig
      ? sections
          .filter((s) =>
            s.queries.every((query) => {
              return isQuerySupported(query, integrationConfig);
            })
          )
          .slice(0, 10)
      : [];
  }, [digests, integrationConfig]);

  const orgId = app.org_id!;

  return (
    <div style={{ padding: 16, marginBottom: 40 }}>
      {pulseItems?.map((section, i) => (
        <Fragment key={i}>
          <DigestSectionCard section={section} orgId={orgId} />
        </Fragment>
      ))}
    </div>
  );
}

const toQuery = (
  card: BoardCardData,
  digest: DigestV2State,
  timeframeOverride?: Pick<SearchState, "duration" | "startDate" | "endDate">,
  filtersOverride?: Condition[]
) => {
  if (card.type !== DeepLinkType.EXPLORE)
    throw new Error("expected question card");

  const defaultDateRange = timeframeOverride || digest.defaultDateRange;

  const filters: Condition[] = [];
  const overriden = new Set<string>();
  filtersOverride?.forEach((f) => {
    filters.push(f);
    overriden.add(f.target);
  });
  digest.defaultFilters?.forEach((f) => {
    if (!overriden.has(f.target)) {
      filters.push(f);
    }
  });

  const globalState = {
    ...defaultDateRange,
    duration: defaultDateRange?.duration || undefined,
    filters,
  };

  const fragment = figureQueryFragment(card, globalState);
  // now join the fragment with the card's state.params
  // the one spot we need to worry about is the card's filters.

  const joinedFilters = [
    ...(card.state.params?.filters || []),
    ...(fragment.filters || []),
  ];
  return { ...card.state.params, ...fragment, filters: joinedFilters };
};

export const toSection = (
  name: string,
  digest: DigestV2State,
  timeframeOverride?: Pick<SearchState, "duration" | "startDate" | "endDate">,
  filtersOverride?: Condition[]
): DigestSection & { name: string } => {
  const { prompt, data } = digest;
  const queryStates: SearchState[] = Object.values(data)
    .filter(isExploreCard)
    .map((card) => toQuery(card, digest, timeframeOverride, filtersOverride));
  return { prompt, queries: queryStates, name };
};
